import { Injectable, NgZone, inject } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import {
  BillboardCollection,
  Cartesian2,
  Cartesian3,
  Cartographic,
  Color,
  HorizontalOrigin,
  LabelCollection,
  LabelStyle,
  NearFarScalar,
  ScreenSpaceEventHandler,
  ScreenSpaceEventType,
  VerticalOrigin,
  Math as CesiumMath,
  type Billboard,
  type Label,
  type Viewer
} from 'cesium';
import { CoresService } from './cores.service';
import type { VehicleMonitorSnapshot, VehicleMonitorVehicle } from './vehicle-monitor.service';

type ViewMode = 'top' | 'side';

export interface VehicleHoverTooltipInfo {
  x: number;
  y: number;
  vehicle: VehicleMonitorVehicle;
  pinned?: boolean;
}

interface MarkerState {
  billboard: Billboard;
  label: Label | undefined;
  pos: Cartesian3;
  target: Cartesian3;
  lastFix: Cartesian3;
  desiredHeading: number;
  sequence: number;
  cycle: number;
  heading: number;
  colorKey: string;
  viewMode: ViewMode;
  faceLeft: boolean;
  trackerId: string;
  vehicle: VehicleMonitorVehicle;
  cruiseMps: number;
  fixAt: number;
}

const SNAP_METERS = 25000;
const TURN_DEG_PER_SEC = 45;
const EARTH_RADIUS = 6_378_137;
const scratchFrom = new Cartographic();
const scratchTo = new Cartographic();
const scratchLerp = new Cartesian3();
const scratchHere = new Cartographic();

@Injectable({ providedIn: 'root' })
export class VehicleMonitorMarkerService {
  private readonly cores = inject(CoresService);
  private readonly ngZone = inject(NgZone);
  private readonly markers = new Map<string, MarkerState>();
  private readonly carIcons = new Map<string, string>();
  private readonly picked = new Subject<string>();
  readonly pickedTrackerId$ = this.picked.asObservable();
  private pickHandler: ScreenSpaceEventHandler | undefined;
  private readonly hoverTooltipSubject = new BehaviorSubject<VehicleHoverTooltipInfo | null>(null);
  readonly hoverTooltip$ = this.hoverTooltipSubject.asObservable();
  private billboards: BillboardCollection | undefined;
  private labels: LabelCollection | undefined;
  private datasetId = '';
  private tickBound = false;
  private animFrame = 0;
  private cameraHeadingDeg = 0;
  private viewMode: ViewMode = 'top';
  private lastHeadingBucket = 0;
  private lastHeightBucket = -1;
  private lastAnimMs = 0;
  private cruising = false;
  private restoredRenderMode: boolean | undefined;
  private focusedTrackerId: string | null = null;
  private hoverPinned = false;
  private focusBillboard: Billboard | undefined;
  private focusReticleUrl: string | undefined;

  apply(snapshot: VehicleMonitorSnapshot, lerp: boolean): void {
    const viewer = this.cores.getViewer() as Viewer | undefined;
    if (!viewer?.scene) {
      return;
    }
    this.ensureCollections(viewer);
    this.ensureAnim();
    this.ensurePick(viewer);
    if (this.datasetId && this.datasetId !== snapshot.id) {
      this.clearMarkers();
    }
    this.datasetId = snapshot.id;
    this.cameraHeadingDeg = CesiumMath.toDegrees(viewer.camera.heading);
    const nextMode = this.readViewMode(viewer);
    const modeChanged = nextMode !== this.viewMode;
    this.viewMode = nextMode;
    if (modeChanged) {
      for (const marker of this.markers.values()) {
        this.applyView(marker, true);
      }
    }
    const ids = new Set(snapshot.vehicles.map((vehicle) => vehicle.trackerId));
    for (const [id, marker] of this.markers) {
      if (!ids.has(id)) {
        this.removeMarker(marker);
        this.markers.delete(id);
      }
    }
    const showLabels = !!snapshot.config.showVehicleLabels && snapshot.vehicles.length <= 30;
    for (const vehicle of snapshot.vehicles) {
      this.upsert(vehicle, snapshot, lerp, showLabels);
    }
    this.cruising = !!lerp;
    viewer.scene.requestRender();
    if (this.focusedTrackerId) {
      this.syncFocusBillboard();
      this.syncPinnedHover();
    }
  }

  clear(): void {
    const viewer = this.cores.getViewer() as Viewer | undefined;
    this.clearFocus();
    this.clearMarkers();
    this.removeCollections(viewer);
    this.datasetId = '';
    this.stopAnim();
    this.lastAnimMs = 0;
    this.cruising = false;
    if (this.restoredRenderMode != null && viewer?.scene) {
      viewer.scene.requestRenderMode = this.restoredRenderMode;
      this.restoredRenderMode = undefined;
    }
    if (this.tickBound) {
      this.cores.removeOnTickListener('vehicle-monitor');
      this.tickBound = false;
    }
    if (this.pickHandler) {
      this.pickHandler.destroy();
      this.pickHandler = undefined;
    }
    this.ngZone.run(() => this.hoverTooltipSubject.next(null));
  }

  focusTracker(trackerId: string | null): void {
    this.focusedTrackerId = trackerId ? String(trackerId) : null;
    if (!this.focusedTrackerId) {
      this.clearFocus();
      return;
    }
    this.hoverPinned = true;
    this.ensureFocusBillboard();
    this.syncFocusBillboard();
    this.syncPinnedHover(true);
  }

  dismissHover(): void {
    this.hoverPinned = false;
    if (this.hoverTooltipSubject.value) {
      this.ngZone.run(() => this.hoverTooltipSubject.next(null));
    }
  }

  clearFocus(): void {
    this.focusedTrackerId = null;
    this.hoverPinned = false;
    if (this.focusBillboard && this.billboards && !this.billboards.isDestroyed()) {
      this.billboards.remove(this.focusBillboard);
    }
    this.focusBillboard = undefined;
    if (this.hoverTooltipSubject.value) {
      this.ngZone.run(() => this.hoverTooltipSubject.next(null));
    }
  }

  private ensureFocusBillboard(): void {
    if (!this.billboards || this.billboards.isDestroyed()) {
      return;
    }
    if (this.focusBillboard) {
      this.focusBillboard.image = this.reticleIcon();
      return;
    }
    const marker = this.focusedTrackerId ? this.markers.get(this.focusedTrackerId) : undefined;
    this.focusBillboard = this.billboards.add({
      id: 'vehicle-monitor-focus',
      position: marker?.pos || Cartesian3.ZERO,
      image: this.reticleIcon(),
      width: 88,
      height: 88,
      alignedAxis: Cartesian3.ZERO,
      disableDepthTestDistance: Number.POSITIVE_INFINITY,
      verticalOrigin: VerticalOrigin.CENTER,
      horizontalOrigin: HorizontalOrigin.CENTER,
      sizeInMeters: false
    });
  }

  private syncPinnedHover(force = false): void {
    if (!this.hoverPinned) {
      return;
    }
    const viewer = this.cores.getViewer() as Viewer | undefined;
    const marker = this.focusedTrackerId ? this.markers.get(this.focusedTrackerId) : undefined;
    if (!viewer?.scene?.canvas || !marker) {
      return;
    }
    const windowPos = viewer.scene.cartesianToCanvasCoordinates(marker.pos);
    if (!windowPos) {
      return;
    }
    const rect = viewer.scene.canvas.getBoundingClientRect();
    const next: VehicleHoverTooltipInfo = {
      x: rect.left + windowPos.x,
      y: rect.top + windowPos.y,
      vehicle: marker.vehicle,
      pinned: true
    };
    const current = this.hoverTooltipSubject.value;
    if (
      !force
      && current
      && current.vehicle.trackerId === next.vehicle.trackerId
      && current.vehicle.speedKph === next.vehicle.speedKph
      && current.vehicle.zone === next.vehicle.zone
      && current.vehicle.campusLabel === next.vehicle.campusLabel
      && Math.abs(current.x - next.x) < 2
      && Math.abs(current.y - next.y) < 2
    ) {
      return;
    }
    this.ngZone.run(() => this.hoverTooltipSubject.next(next));
  }

  private syncFocusBillboard(): void {
    if (!this.focusBillboard) {
      this.ensureFocusBillboard();
    }
    const marker = this.focusedTrackerId ? this.markers.get(this.focusedTrackerId) : undefined;
    if (!this.focusBillboard || !marker) {
      if (this.focusBillboard) {
        this.focusBillboard.show = false;
      }
      return;
    }
    this.focusBillboard.show = true;
    this.focusBillboard.position = marker.pos;
  }

  private reticleIcon(): string {
    const size = 256;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return this.focusReticleUrl || '';
    }
    const cx = size / 2;
    const cy = size / 2;
    const hot = '#ff4d4d';
    const deep = '#c62828';

    const glow = ctx.createRadialGradient(cx, cy, 8, cx, cy, 118);
    glow.addColorStop(0, 'rgba(255, 80, 80, 0.35)');
    glow.addColorStop(0.45, 'rgba(229, 57, 53, 0.14)');
    glow.addColorStop(1, 'rgba(198, 40, 40, 0)');
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(cx, cy, 118, 0, Math.PI * 2);
    ctx.fill();

    const stroke = ctx.createRadialGradient(cx, cy, 4, cx, cy, 130);
    stroke.addColorStop(0, hot);
    stroke.addColorStop(0.55, '#e53935');
    stroke.addColorStop(1, deep);
    ctx.strokeStyle = stroke;
    ctx.shadowColor = 'rgba(229, 57, 53, 0.85)';
    ctx.shadowBlur = 10;
    ctx.lineCap = 'square';
    ctx.lineJoin = 'miter';

    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(18, cy);
    ctx.lineTo(size - 18, cy);
    ctx.moveTo(cx, 18);
    ctx.lineTo(cx, size - 18);
    ctx.stroke();

    const tickGaps = [18, 32, 46];
    const tickHalf = [7, 10, 14];
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    tickGaps.forEach((gap, i) => {
      const half = tickHalf[i];
      ctx.moveTo(cx - gap, cy - half);
      ctx.lineTo(cx - gap, cy + half);
      ctx.moveTo(cx + gap, cy - half);
      ctx.lineTo(cx + gap, cy + half);
      ctx.moveTo(cx - half, cy - gap);
      ctx.lineTo(cx + half, cy - gap);
      ctx.moveTo(cx - half, cy + gap);
      ctx.lineTo(cx + half, cy + gap);
    });
    ctx.stroke();

    const inset = 48;
    const arm = 42;
    ctx.lineWidth = 8;
    ctx.beginPath();
    ctx.moveTo(inset, inset + arm);
    ctx.lineTo(inset, inset);
    ctx.lineTo(inset + arm, inset);
    ctx.moveTo(size - inset - arm, inset);
    ctx.lineTo(size - inset, inset);
    ctx.lineTo(size - inset, inset + arm);
    ctx.moveTo(inset, size - inset - arm);
    ctx.lineTo(inset, size - inset);
    ctx.lineTo(inset + arm, size - inset);
    ctx.moveTo(size - inset - arm, size - inset);
    ctx.lineTo(size - inset, size - inset);
    ctx.lineTo(size - inset, size - inset - arm);
    ctx.stroke();

    ctx.shadowBlur = 0;
    this.focusReticleUrl = canvas.toDataURL('image/png');
    return this.focusReticleUrl;
  }

  private ensureCollections(viewer: Viewer): void {
    if (!this.billboards || this.billboards.isDestroyed()) {
      this.billboards = viewer.scene.primitives.add(new BillboardCollection({
        scene: viewer.scene
      })) as BillboardCollection;
    }
    if (!this.labels || this.labels.isDestroyed()) {
      this.labels = viewer.scene.primitives.add(new LabelCollection({
        scene: viewer.scene
      })) as LabelCollection;
    }
  }

  private removeCollections(viewer: Viewer | undefined): void {
    if (this.billboards && viewer?.scene?.primitives) {
      viewer.scene.primitives.remove(this.billboards);
    }
    if (this.labels && viewer?.scene?.primitives) {
      viewer.scene.primitives.remove(this.labels);
    }
    this.billboards = undefined;
    this.labels = undefined;
  }

  private clearMarkers(): void {
    for (const marker of this.markers.values()) {
      this.removeMarker(marker);
    }
    this.markers.clear();
  }

  private removeMarker(marker: MarkerState): void {
    if (this.billboards && !this.billboards.isDestroyed()) {
      this.billboards.remove(marker.billboard);
    }
    if (marker.label && this.labels && !this.labels.isDestroyed()) {
      this.labels.remove(marker.label);
    }
  }

  private ensureAnim(): void {
    const viewer = this.cores.getViewer() as Viewer | undefined;
    if (viewer?.scene && this.restoredRenderMode == null) {
      this.restoredRenderMode = viewer.scene.requestRenderMode;
      viewer.scene.requestRenderMode = false;
    }
    if (this.animFrame) {
      return;
    }
    const step = () => {
      this.animFrame = requestAnimationFrame(step);
      this.tickMarkers();
    };
    this.animFrame = requestAnimationFrame(step);
  }

  private stopAnim(): void {
    if (this.animFrame) {
      cancelAnimationFrame(this.animFrame);
      this.animFrame = 0;
    }
  }

  private tickMarkers(): void {
    const viewer = this.cores.getViewer() as Viewer | undefined;
    if (!viewer) {
      return;
    }
    const now = performance.now();
    const dt = this.lastAnimMs ? Math.min(0.05, (now - this.lastAnimMs) / 1000) : 0.016;
    this.lastAnimMs = now;
    this.cameraHeadingDeg = CesiumMath.toDegrees(viewer.camera.heading);
    const next = this.readViewMode(viewer);
    const headingBucket = Math.round(this.cameraHeadingDeg / 10);
    const heightBucket = Math.round((viewer.camera.positionCartographic?.height || 0) / 50000);
    const modeChanged = next !== this.viewMode;
    const zoomChanged = heightBucket !== this.lastHeightBucket;
    this.viewMode = next;
    this.lastHeightBucket = heightBucket;
    let moved = false;
    for (const marker of this.markers.values()) {
      if (this.stepMarker(marker, dt)) {
        moved = true;
      }
      if (modeChanged || zoomChanged) {
        this.applyView(marker, modeChanged);
      } else if (next === 'top') {
        marker.billboard.rotation = this.billboardRotation(marker);
      }
    }
    this.lastHeadingBucket = headingBucket;
    this.syncFocusBillboard();
    if (this.hoverPinned) {
      this.syncPinnedHover();
    }
    if (moved || modeChanged || zoomChanged || this.focusedTrackerId) {
      viewer.scene?.requestRender();
    }
  }

  private ensurePick(viewer: Viewer): void {
    if (this.pickHandler || !viewer.scene?.canvas) {
      return;
    }
    this.pickHandler = new ScreenSpaceEventHandler(viewer.scene.canvas);
    this.pickHandler.setInputAction((click: { position?: Cartesian2 }) => {
      if (!click?.position) {
        return;
      }
      const hit = viewer.scene.pick(click.position);
      const trackerId = this.trackerIdFromPick(hit);
      if (trackerId) {
        this.picked.next(trackerId);
      }
    }, ScreenSpaceEventType.LEFT_CLICK);

    let pending: Cartesian2 | null = null;
    let raf = false;
    this.pickHandler.setInputAction((movement: { endPosition?: Cartesian2 }) => {
      pending = movement.endPosition || null;
      if (raf) {
        return;
      }
      raf = true;
      requestAnimationFrame(() => {
        raf = false;
        const pos = pending;
        pending = null;
        if (!pos) {
          return;
        }
        if (this.hoverPinned) {
          return;
        }
        const trackerId = this.trackerIdFromPick(viewer.scene.pick(pos));
        const marker = trackerId ? this.markers.get(trackerId) : undefined;
        const rect = viewer.scene.canvas.getBoundingClientRect();
        this.ngZone.run(() => {
          if (!marker) {
            if (this.hoverTooltipSubject.value) {
              this.hoverTooltipSubject.next(null);
            }
            return;
          }
          this.hoverTooltipSubject.next({
            x: rect.left + pos.x,
            y: rect.top + pos.y,
            vehicle: marker.vehicle
          });
        });
      });
    }, ScreenSpaceEventType.MOUSE_MOVE);
  }

  private trackerIdFromPick(hit: { id?: unknown }): string | null {
    const raw = hit?.id;
    if (typeof raw === 'string' && raw.startsWith('vehicle-monitor:')) {
      return raw.slice('vehicle-monitor:'.length);
    }
    return null;
  }

  private readViewMode(viewer: Viewer): ViewMode {
    const pitch = viewer.camera.pitch;
    if (this.viewMode === 'top') {
      return pitch > -1.35 ? 'side' : 'top';
    }
    return pitch < -1.55 ? 'top' : 'side';
  }

  private facesLeft(heading: number): boolean {
    const delta = ((heading - this.cameraHeadingDeg + 540) % 360) - 180;
    return Math.sin(delta * Math.PI / 180) < 0;
  }

  private upsert(
    vehicle: VehicleMonitorVehicle,
    snapshot: VehicleMonitorSnapshot,
    lerp: boolean,
    showLabels: boolean
  ): void {
    if (!this.billboards) {
      return;
    }
    const target = Cartesian3.fromDegrees(vehicle.longitude, vehicle.latitude, 12);
    const registered = !!vehicle.registration || vehicle.authorized === true;
    const paintKey = registered ? 'known' : 'unknown';
    const labelText = registered ? (vehicle.vehicleNo || vehicle.trackerId) : `UNKNOWN · ${vehicle.trackerId}`;
    let marker = this.markers.get(vehicle.trackerId);
    const isNew = !marker;

    // Reject stale or out-of-order sequence packets caused by network latency jitter
    if (marker && snapshot.simulation?.cycle === marker.cycle && vehicle.sequence < marker.sequence) {
      return;
    }

    if (!marker) {
      const heading = vehicle.heading || 0;
      const side = this.viewMode === 'side';
      const billboard = this.billboards.add({
        id: `vehicle-monitor:${vehicle.trackerId}`,
        position: target,
        image: this.carIcon(paintKey, side, this.facesLeft(heading)),
        width: side ? 56 : this.topIconPx(),
        height: side ? 28 : this.topIconPx(),
        alignedAxis: Cartesian3.ZERO,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
        scaleByDistance: new NearFarScalar(4.0e4, 1.0, 1.8e6, 1.79),
        verticalOrigin: side ? VerticalOrigin.BOTTOM : VerticalOrigin.CENTER,
        sizeInMeters: false
      });
      marker = {
        billboard,
        label: undefined,
        pos: Cartesian3.clone(target),
        target: Cartesian3.clone(target),
        lastFix: Cartesian3.clone(target),
        sequence: vehicle.sequence,
        cycle: snapshot.simulation.cycle,
        heading,
        desiredHeading: heading,
        colorKey: paintKey,
        viewMode: this.viewMode,
        faceLeft: this.facesLeft(heading),
        trackerId: vehicle.trackerId,
        vehicle,
        cruiseMps: 70,
        fixAt: 0
      };
      this.markers.set(vehicle.trackerId, marker);
      this.applyView(marker, true);
    }

    const jump = Cartesian3.distance(marker.pos, target);
    const cycleReset = snapshot.simulation.cycle !== marker.cycle && jump > 200;
    const shouldSnap = isNew || !lerp || jump > 2500 || cycleReset;

    marker.lastFix = Cartesian3.clone(target);
    marker.sequence = vehicle.sequence;
    marker.cycle = snapshot.simulation.cycle;
    marker.target = Cartesian3.clone(target);
    marker.vehicle = vehicle;

    if (vehicle.heading != null && !Number.isNaN(vehicle.heading)) {
      marker.desiredHeading = vehicle.heading;
    }

    if (shouldSnap) {
      marker.pos = Cartesian3.clone(target);
      marker.billboard.position = marker.pos;
      marker.heading = vehicle.heading || marker.heading;
      marker.desiredHeading = marker.heading;
      if (marker.label) {
        marker.label.position = marker.pos;
      }
    }

    if (marker.colorKey !== paintKey || marker.viewMode !== this.viewMode) {
      marker.colorKey = paintKey;
      this.applyView(marker, true);
    } else if (shouldSnap) {
      marker.billboard.rotation = this.billboardRotation(marker);
    }
    this.syncLabel(marker, labelText, showLabels);
  }

  private stepMarker(marker: MarkerState, dt: number): boolean {
    const dist = Cartesian3.distance(marker.pos, marker.target);
    if (dist < 0.05) {
      const headingDiff = Math.abs(((marker.desiredHeading - marker.heading + 540) % 360) - 180);
      if (headingDiff > 0.5) {
        marker.heading = this.turnToward(marker.heading, marker.desiredHeading, 360 * dt);
        if (this.viewMode === 'top') {
          marker.billboard.rotation = this.billboardRotation(marker);
        }
        return true;
      }
      return false;
    }
    if (dist > SNAP_METERS) {
      marker.pos = Cartesian3.clone(marker.target);
      marker.billboard.position = marker.pos;
      if (marker.label) {
        marker.label.position = marker.pos;
      }
      return true;
    }

    // Smooth exponential decay interpolation:
    // With rate ~10, smoothly covers the distance between snapshot updates with zero stutter
    const factor = 1 - Math.exp(-10 * dt);
    const speedMps = marker.vehicle?.speedKph ? (marker.vehicle.speedKph * 1000 / 3600) : 0;
    const minStep = speedMps > 0 ? (speedMps * dt * 1.2) : 0;
    const stepDist = Math.min(dist, Math.max(dist * factor, minStep));
    const t = Math.min(1, stepDist / dist);

    Cartesian3.lerp(marker.pos, marker.target, t, scratchLerp);

    const along = this.headingAlong(marker.pos, scratchLerp);
    const targetHeading = marker.desiredHeading != null && !Number.isNaN(marker.desiredHeading)
      ? marker.desiredHeading
      : along;
    if (targetHeading != null) {
      marker.heading = this.turnToward(marker.heading, targetHeading, 360 * dt);
    }

    marker.pos = Cartesian3.clone(scratchLerp);
    marker.billboard.position = marker.pos;
    if (marker.label) {
      marker.label.position = marker.pos;
    }

    if (this.viewMode === 'top') {
      marker.billboard.rotation = this.billboardRotation(marker);
    } else {
      const faceLeft = this.facesLeft(marker.heading);
      if (faceLeft !== marker.faceLeft) {
        marker.faceLeft = faceLeft;
        marker.billboard.image = this.carIcon(marker.colorKey, true, faceLeft);
      }
    }
    return true;
  }

  private advance(from: Cartesian3, headingDeg: number, meters: number): Cartesian3 {
    Cartographic.fromCartesian(from, undefined, scratchHere);
    const rad = CesiumMath.toRadians(headingDeg);
    const lat = scratchHere.latitude + (meters * Math.cos(rad)) / EARTH_RADIUS;
    const lon = scratchHere.longitude + (meters * Math.sin(rad)) / (EARTH_RADIUS * Math.max(0.2, Math.cos(scratchHere.latitude)));
    return Cartesian3.fromDegrees(CesiumMath.toDegrees(lon), CesiumMath.toDegrees(lat), 12);
  }

  private turnToward(from: number, to: number, maxDeg: number): number {
    const delta = ((to - from + 540) % 360) - 180;
    if (Math.abs(delta) <= maxDeg) {
      return (from + delta + 360) % 360;
    }
    return (from + Math.sign(delta) * maxDeg + 360) % 360;
  }

  private topIconPx(): number {
    const viewer = this.cores.getViewer() as Viewer | undefined;
    const height = viewer?.camera?.positionCartographic?.height ?? 8000;
    if (height > 800000) {
      return 13;
    }
    if (height > 250000) {
      return 16;
    }
    if (height > 80000) {
      return 26;
    }
    if (height > 40000) {
      return 31;
    }
    return 42;
  }

  private applyView(marker: MarkerState, forceImage: boolean): void {
    const side = this.viewMode === 'side';
    const faceLeft = side ? this.facesLeft(marker.heading) : false;
    const imageChanged = forceImage || marker.viewMode !== this.viewMode || marker.faceLeft !== faceLeft;
    marker.viewMode = this.viewMode;
    marker.faceLeft = faceLeft;
    if (imageChanged) {
      marker.billboard.image = this.carIcon(marker.colorKey, side, faceLeft);
    }
    marker.billboard.width = side ? 56 : this.topIconPx();
    marker.billboard.height = side ? 28 : this.topIconPx();
    marker.billboard.scaleByDistance = new NearFarScalar(4.0e4, 1.0, 1.8e6, 1.79);
    marker.billboard.verticalOrigin = side ? VerticalOrigin.BOTTOM : VerticalOrigin.CENTER;
    marker.billboard.rotation = this.billboardRotation(marker);
    if (marker.label) {
      marker.label.pixelOffset = new Cartesian2(0, side ? -28 : -20);
    }
  }

  private billboardRotation(marker: MarkerState): number {
    if (this.viewMode !== 'top') {
      return 0;
    }
    return -CesiumMath.toRadians(marker.heading - this.cameraHeadingDeg);
  }

  private syncLabel(marker: MarkerState, text: string, showLabels: boolean): void {
    if (!showLabels) {
      if (marker.label && this.labels && !this.labels.isDestroyed()) {
        this.labels.remove(marker.label);
      }
      marker.label = undefined;
      return;
    }
    if (!this.labels) {
      return;
    }
    if (!marker.label) {
      marker.label = this.labels.add({
        position: marker.pos,
        text,
        font: '11px sans-serif',
        fillColor: Color.WHITE,
        outlineColor: Color.BLACK,
        outlineWidth: 3,
        style: LabelStyle.FILL_AND_OUTLINE,
        verticalOrigin: VerticalOrigin.BOTTOM,
        horizontalOrigin: HorizontalOrigin.CENTER,
        pixelOffset: new Cartesian2(0, this.viewMode === 'side' ? -26 : -20),
        disableDepthTestDistance: Number.POSITIVE_INFINITY
      });
      return;
    }
    marker.label.text = text;
    marker.label.position = marker.pos;
    const viewerForLabel = this.cores.getViewer() as Viewer | undefined;
    const cameraHeight = viewerForLabel?.camera?.positionCartographic?.height;
    marker.label.show = cameraHeight === undefined ? true : cameraHeight < 80000;
  }

  private paint(paintKey: string): { body: string; glass: string; trim: string } {
    const authorized = paintKey !== 'unknown';
    return {
      body: authorized ? '#22c55e' : '#e53935',
      glass: authorized ? '#166534' : '#7f1d1d',
      trim: authorized ? '#86efac' : '#ffcdd2'
    };
  }

  private carIcon(paintKey: string, side: boolean, faceLeft: boolean): string {
    const cacheKey = `${paintKey}:${side ? 'side' : 'top'}:${faceLeft ? 'l' : 'r'}:v11`;
    const cached = this.carIcons.get(cacheKey);
    if (cached) {
      return cached;
    }
    const canvas = side ? this.drawSideCar(paintKey, faceLeft) : this.drawTopCar(paintKey);
    const url = canvas.toDataURL('image/png');
    this.carIcons.set(cacheKey, url);
    return url;
  }

  private drawTopCar(paintKey: string): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return canvas;
    }
    const {body, glass, trim} = this.paint(paintKey);
    ctx.fillStyle = '#111111';
    ctx.fillRect(14, 16, 6, 12);
    ctx.fillRect(44, 16, 6, 12);
    ctx.fillRect(14, 36, 6, 12);
    ctx.fillRect(44, 36, 6, 12);
    ctx.fillStyle = body;
    ctx.beginPath();
    ctx.roundRect(18, 8, 28, 48, 4);
    ctx.fill();
    ctx.strokeStyle = trim;
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.fillStyle = glass;
    ctx.fillRect(22, 14, 20, 14);
    ctx.fillRect(22, 36, 20, 10);
    ctx.fillStyle = '#d8deea';
    ctx.fillRect(22, 10, 6, 4);
    ctx.fillRect(36, 10, 6, 4);
    ctx.fillStyle = '#c45c56';
    ctx.fillRect(22, 50, 6, 3);
    ctx.fillRect(36, 50, 6, 3);
    return canvas;
  }

  private drawSideCar(paintKey: string, faceLeft: boolean): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = 128;
    canvas.height = 64;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return canvas;
    }
    if (faceLeft) {
      ctx.translate(128, 0);
      ctx.scale(-1, 1);
    }
    const {body, glass, trim} = this.paint(paintKey);
    ctx.fillStyle = '#1a1a1a';
    ctx.beginPath();
    ctx.ellipse(34, 50, 11, 11, 0, 0, Math.PI * 2);
    ctx.ellipse(94, 50, 11, 11, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#4a4a4a';
    ctx.beginPath();
    ctx.ellipse(34, 50, 5, 5, 0, 0, Math.PI * 2);
    ctx.ellipse(94, 50, 5, 5, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = body;
    ctx.beginPath();
    ctx.roundRect(14, 22, 102, 26, 6);
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(36, 22);
    ctx.lineTo(48, 8);
    ctx.lineTo(82, 8);
    ctx.lineTo(96, 22);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = trim;
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.fillStyle = glass;
    ctx.beginPath();
    ctx.moveTo(40, 22);
    ctx.lineTo(50, 11);
    ctx.lineTo(78, 11);
    ctx.lineTo(90, 22);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = '#d8deea';
    ctx.fillRect(108, 28, 6, 8);
    ctx.fillStyle = '#c45c56';
    ctx.fillRect(14, 28, 5, 8);
    return canvas;
  }

  private headingAlong(from: Cartesian3, to: Cartesian3): number | null {
    if (Cartesian3.distance(from, to) < 0.4) {
      return null;
    }
    Cartographic.fromCartesian(from, undefined, scratchFrom);
    Cartographic.fromCartesian(to, undefined, scratchTo);
    const east = scratchTo.longitude - scratchFrom.longitude;
    const north = scratchTo.latitude - scratchFrom.latitude;
    return CesiumMath.toDegrees(Math.atan2(east, north));
  }
}
